import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
  Tabs,
  Tab,
  List,
  ListItemIcon,
  Card,
  CardContent,
  Badge,
  TextField,
  Grid,
} from '@mui/material';
import {
  Close,
  Visibility,
  VisibilityOff,
  Person,
  AccessTime,
  Info,
  History,
  GetApp,
  OpenInNew,
  Email,
  Send,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`action-tabpanel-${index}`}
      aria-labelledby={`action-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface ActionModalProps {
  open: boolean;
  onClose: () => void;
  item: any;
  type: 'legislation' | 'change';
  monitoringHistory?: any[];
  isMonitored?: boolean;
  onToggleMonitoring?: (itemId: number) => void;
  onSendEmail?: (emailData: { to: string; subject: string; item: any }) => Promise<void>;
  modalProps?: {
    title?: string;
    showEmail?: boolean;
    showDownload?: boolean;
    showHistory?: boolean;
    showMonitoring?: boolean;
  };
}

export function ActionModal({
  open,
  onClose,
  item,
  type,
  monitoringHistory = [],
  isMonitored = false,
  onToggleMonitoring,
  onSendEmail,
  modalProps = {},
}: ActionModalProps) {
  const [tabValue, setTabValue] = useState(0);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSendEmailClick = () => {
    setEmailDialogOpen(true);
  };

  const handleSendEmailSubmit = async () => {
    if (!emailTo || !emailSubject || !onSendEmail) return;

    setSendingEmail(true);
    try {
      await onSendEmail({
        to: emailTo,
        subject: emailSubject,
        item,
      });
      setEmailDialogOpen(false);
      setEmailTo('');
      setEmailSubject('');
    } catch (error) {
      console.error('Erro ao enviar email:', error);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmailTo('');
    setEmailSubject('');
  };

  if (!item) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {type === 'legislation' ? 'Detalhes da Legislação' : 'Detalhes da Mudança'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {item.id}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />

        {type === 'legislation' ? (
          <DialogContent sx={{ pt: 2 }}>
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Estado
                  </Typography>
                  <Chip
                    label={item.state || 'N/A'}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Agência
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.source?.name || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Tipo de Norma
                  </Typography>
                  <Chip
                    label={item.type || 'N/A'}
                    size="small"
                    color="info"
                    sx={{ fontWeight: 600 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Número
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {item.number}/{item.year}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Título
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {item.title}
                </Typography>
              </Box>

              {item.summary && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Resumo
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {item.summary}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Data de Criação
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {item.created_at_official ? format(new Date(item.created_at_official), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Última Atualização
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {format(new Date(item.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </Typography>
                </Grid>
              </Grid>

              {item.url && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<OpenInNew />}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                  >
                    Ver Documento Original
                  </Button>
                </Box>
              )}

              {/* Status de Monitoramento */}
              {onToggleMonitoring && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant={isMonitored ? "outlined" : "contained"}
                    color={isMonitored ? "error" : "success"}
                    startIcon={isMonitored ? <VisibilityOff /> : <Visibility />}
                    onClick={() => onToggleMonitoring(item.id)}
                    fullWidth
                  >
                    {isMonitored ? 'Desativar Monitoramento' : 'Ativar Monitoramento'}
                  </Button>
                </Box>
              )}

              {/* Histórico de Monitoramento */}
              {monitoringHistory && monitoringHistory.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Histórico de Monitoramento
                  </Typography>
                  <List dense>
                    {monitoringHistory.slice(0, 5).map((record: any, index: number) => (
                      <Box key={record.id || index} sx={{ mb: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={record.is_active ? 'Ativo' : 'Inativo'}
                            size="small"
                            color={record.is_active ? 'success' : 'default'}
                            variant="outlined"
                          />
                          <Typography variant="body2">
                            {record.monitored_by_name || 'Sistema'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(record.started_at || record.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </DialogContent>
        ) : (
          <DialogContent sx={{ pt: 2 }}>
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Estado
                  </Typography>
                  <Chip
                    label={item.state || 'N/A'}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Agência
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.agency || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Tipo de Norma
                  </Typography>
                  <Chip
                    label={item.type || 'N/A'}
                    size="small"
                    color="info"
                    sx={{ fontWeight: 600 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Número
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {item.number || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Título
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {item.title || 'N/A'}
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Data de Detecção
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {item.date ? format(new Date(item.date), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vigência
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {item.effective_date ? format(new Date(item.effective_date), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Tipo de Mudança
                </Typography>
                <Chip
                  label={item.change || 'N/A'}
                  size="small"
                  color={
                    item.change === 'Criada' ? 'success' :
                    item.change === 'Atualizada' ? 'info' :
                    'error'
                  }
                />
              </Box>

              {item.summary && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Resumo
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {item.summary}
                  </Typography>
                </Box>
              )}

              {item.url && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<OpenInNew />}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                  >
                    Ver Documento Original
                  </Button>
                </Box>
              )}

              {modalProps?.showEmail !== false && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<Email />}
                    onClick={handleSendEmailClick}
                    fullWidth
                  >
                    Enviar por E-mail
                  </Button>
                </Box>
              )}
            </Box>
          </DialogContent>
        )}

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Enviar Mudança por E-mail
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="E-mail do Destinatário"
            type="email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Assunto"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            Será enviado um e-mail com os detalhes da mudança selecionada.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEmailDialog} variant="outlined" disabled={sendingEmail}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendEmailSubmit}
            variant="contained"
            startIcon={sendingEmail ? null : <Send />}
            disabled={!emailTo || !emailSubject || sendingEmail}
          >
            {sendingEmail ? 'Enviando...' : 'Enviar E-mail'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
